// Market structure on any timeframe: the swing highs and lows (labelled
// HH / LH / HL / LL), the trend they make, and its channel. A reading aid for
// the chart's own candles; the engine's decisions stay on the 1h with its
// 4h bias.
import type { Candle } from "./indicators";

export type SwingLabel = "H" | "L" | "HH" | "LH" | "EH" | "HL" | "LL" | "EL";

export interface Swing {
  time: number;
  price: number;
  kind: "high" | "low";
  /** Against the swing of the same kind before it; the first is plain H / L. */
  label: SwingLabel;
  /** false: the leg still running (price hasn't turned far enough to confirm it). */
  confirmed: boolean;
}

/** A swing counts once price turns this many ATRs away from it. */
export const SWING_ATR = 2.5;
const ATR_PERIOD = 14;

/** Average true range at each bar: the mean of the last `period` true ranges (null before 2 bars). */
function atrAt(candles: Candle[], period = ATR_PERIOD): (number | null)[] {
  const out: (number | null)[] = [];
  let sum = 0;
  const trs: number[] = [];
  for (let i = 0; i < candles.length; i++) {
    const c = candles[i]!;
    const prev = candles[i - 1];
    const tr = prev ? Math.max(c.high - c.low, Math.abs(c.high - prev.close), Math.abs(c.low - prev.close)) : c.high - c.low;
    trs.push(tr);
    sum += tr;
    if (trs.length > period) sum -= trs[trs.length - 1 - period]!;
    out.push(sum / Math.min(trs.length, period));
  }
  return out;
}

/**
 * Swing highs and lows by the ATR zigzag: a high is confirmed once price has
 * fallen `mult` ATRs from it (a low once it has risen that far), so swings
 * alternate high, low, high and small wiggles don't count. The last swing is
 * the extreme of the leg still running, unconfirmed.
 */
export function swings(candles: Candle[], mult = SWING_ATR): Swing[] {
  if (candles.length < 2) return [];
  const atr = atrAt(candles);
  const raw: { i: number; price: number; kind: "high" | "low" }[] = [];
  let dir: 1 | -1 | 0 = 0;
  let hi = { i: 0, price: candles[0]!.high };
  let lo = { i: 0, price: candles[0]!.low };
  for (let i = 1; i < candles.length; i++) {
    const c = candles[i]!;
    const turn = mult * atr[i]!;
    if (dir >= 0 && c.high > hi.price) {
      hi = { i, price: c.high };
      if (dir === 1) continue; // a new high in a rising leg: no turn on the same candle
    }
    if (dir <= 0 && c.low < lo.price) {
      lo = { i, price: c.low };
      if (dir === -1) continue;
    }
    if (dir >= 0 && hi.price - c.low >= turn && hi.i < i) {
      // Fell far enough from the high: it is a swing high (and the start's low a swing low before it).
      if (dir === 0 && lo.i < hi.i) raw.push({ ...lo, kind: "low" });
      raw.push({ ...hi, kind: "high" });
      dir = -1;
      lo = { i, price: c.low };
    } else if (dir <= 0 && c.high - lo.price >= turn && lo.i < i) {
      if (dir === 0 && hi.i < lo.i) raw.push({ ...hi, kind: "high" });
      raw.push({ ...lo, kind: "low" });
      dir = 1;
      hi = { i, price: c.high };
    }
  }
  const open = dir === 1 ? { ...hi, kind: "high" as const } : dir === -1 ? { ...lo, kind: "low" as const } : null;
  const all = open ? [...raw, open] : raw;

  let lastHigh: number | null = null;
  let lastLow: number | null = null;
  return all.map((s, k) => {
    const prev = s.kind === "high" ? lastHigh : lastLow;
    const label: SwingLabel = s.kind === "high"
      ? prev === null ? "H" : s.price > prev ? "HH" : s.price < prev ? "LH" : "EH"
      : prev === null ? "L" : s.price > prev ? "HL" : s.price < prev ? "LL" : "EL";
    if (s.kind === "high") lastHigh = s.price; else lastLow = s.price;
    return { time: candles[s.i]!.time, price: s.price, kind: s.kind, label, confirmed: k < raw.length };
  });
}

export interface StructureRead {
  trend: "up" | "down" | "range";
  /** The swing that would break the trend: the last higher low (up) or lower high (down). */
  pivot: Swing | null;
  /** The last close went past `pivot`: the trend is in doubt. */
  broken: boolean;
}

/**
 * The trend the confirmed swings make: higher highs and higher lows = up,
 * lower highs and lower lows = down, anything else a range. The last close
 * beyond the last higher low (lower high) breaks it.
 */
export function readStructure(all: Swing[], lastClose: number): StructureRead {
  const done = all.filter(s => s.confirmed);
  const highs = done.filter(s => s.kind === "high").slice(-2);
  const lows = done.filter(s => s.kind === "low").slice(-2);
  if (highs.length < 2 || lows.length < 2) return { trend: "range", pivot: null, broken: false };
  const [h1, h2] = highs as [Swing, Swing];
  const [l1, l2] = lows as [Swing, Swing];
  if (h2.price > h1.price && l2.price > l1.price) return { trend: "up", pivot: l2, broken: lastClose < l2.price };
  if (h2.price < h1.price && l2.price < l1.price) return { trend: "down", pivot: h2, broken: lastClose > h2.price };
  return { trend: "range", pivot: null, broken: false };
}

export interface Channel {
  /** The trend line through the last two higher lows (up) or lower highs (down)… */
  t1: number; p1: number; t2: number; p2: number;
  /** …and its parallel, this far above (up) or below (down) in price, through the furthest swing since. */
  offset: number;
}

/** The trend's channel, like one drawn by hand: a line on its last two swings of the trend side, a parallel on the other. */
export function trendChannel(all: Swing[], trend: StructureRead["trend"]): Channel | null {
  if (trend === "range") return null;
  const done = all.filter(s => s.confirmed);
  const base = done.filter(s => s.kind === (trend === "up" ? "low" : "high")).slice(-2);
  if (base.length < 2) return null;
  const [a, b] = base as [Swing, Swing];
  if (b.time === a.time) return null;
  const slope = (b.price - a.price) / (b.time - a.time);
  const at = (t: number) => a.price + slope * (t - a.time);
  // The other side: every swing since the first anchor, the furthest from the line.
  const other = all.filter(s => s.time >= a.time && s.kind === (trend === "up" ? "high" : "low"));
  if (other.length === 0) return null;
  const offsets = other.map(s => s.price - at(s.time));
  const offset = trend === "up" ? Math.max(...offsets) : Math.min(...offsets);
  return { t1: a.time, p1: a.price, t2: b.time, p2: b.price, offset };
}
